import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'da', label: 'DA' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <nav className="sticky top-0 z-50 w-full bg-background-50 border-b-2 border-background-300">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading font-extrabold text-lg md:text-xl text-foreground-950 tracking-tight">
              AllChinaBuy<span className="text-primary-500">Spread</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link to="/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>
            <Link to="/article" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
              {t('nav.shoppingGuide')}
            </Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border-2 border-background-300 py-2 max-h-96 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameKey}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-800 hover:bg-background-100 hover:text-primary-500 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <img src={cat.imageUrl} alt={cat.name} className="w-8 h-8 rounded-md object-cover shrink-0" />
                      <span>{t(`categories.${cat.nameKey}`)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="flex items-center gap-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`px-2 py-1 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-700 hover:text-primary-500'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav.explore')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold border-2 border-primary-500 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              {t('nav.buyNow')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t-2 border-background-300 bg-background-50 px-4 py-4 space-y-3 max-h-[80vh] overflow-y-auto">
          {/* Primary links */}
          <div className="space-y-1">
            <Link to="/" className="flex items-center gap-3 px-3 py-3 text-base font-semibold text-foreground-900 rounded-lg hover:bg-background-100" onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-home-5-line text-primary-500" />
              {t('nav.home')}
            </Link>
            <Link to="/article" className="flex items-center gap-3 px-3 py-3 text-base font-semibold text-foreground-900 rounded-lg hover:bg-background-100" onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-book-open-line text-primary-500" />
              {t('nav.shoppingGuide')}
            </Link>
          </div>

          {/* Categories toggle */}
          <div className="border-t border-background-200 pt-3">
            <button
              onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
              className="flex items-center justify-between w-full px-3 py-3 text-base font-semibold text-foreground-900 rounded-lg hover:bg-background-100"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-primary-500" />
                {t('nav.categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileCatsOpen && (
              <div className="grid grid-cols-2 gap-2 pt-2 pb-1">
                {categories.map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2 py-2 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <img src={cat.imageUrl} alt={cat.name} className="w-9 h-9 rounded-md object-cover shrink-0" />
                    <span className="text-xs font-medium text-foreground-800 leading-tight">{t(`categories.${cat.nameKey}`)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Language Switcher */}
          <div className="border-t border-background-200 pt-3">
            <p className="text-xs font-semibold text-foreground-500 mb-2">Language</p>
            <div className="flex gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col gap-2 pt-2 border-t border-background-200">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block text-center px-4 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg whitespace-nowrap"
            >
              {t('nav.explore')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block text-center px-4 py-3 text-sm font-semibold border-2 border-primary-500 text-primary-600 rounded-lg whitespace-nowrap"
            >
              {t('nav.buyNow')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}